import { api } from "@breakout/backend/convex/_generated/api"
import { prepareClaim } from "@breakout/sdk"
import { toast } from "@breakout/ui/components/Toast"
import { useWallets } from "@privy-io/react-auth"
import { useQueryClient } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useCallback, useState } from "react"
import type { Address, Hex } from "viem"
import { getAddress, isAddress } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { captureTradeError } from "../../lib/sentry"
import { serializeClaimPreparation } from "../../lib/tradeUtils"
import { createWalletClientFromWallet, findEmbeddedWallet } from "../../lib/wallet"
import { marketKeys } from "../market/useMarket"
import { walletKeys } from "../wallet/useUsdcBalance"

type ClaimAllStatus = "idle" | "preparing" | "signing" | "executing" | "completed" | "error"

interface ClaimAllProgress {
	/** Current step description */
	step: string
	/** Current item being processed */
	current: number
	/** Total items to process */
	total: number
	/** Total amount claimed so far */
	amountClaimed: number
	/** Market addresses that have been claimed */
	claimedMarkets: Address[]
	/** Any errors encountered */
	errors: Array<{ market: Address; error: string }>
}

interface ClaimablePosition {
	marketAddress: Address
	claimableAmount: number
}

interface PreparedClaim {
	marketAddress: Address
	preparation: {
		type: "claim"
		signingPayload: {
			domain: {
				name: string
				version?: string
				chainId: number
				verifyingContract: `0x${string}`
			}
			types: Record<string, readonly { type: string; name: string }[]>
			primaryType: string
			message: Record<string, unknown>
		}
		claimable: number
		expiresAt: Date
		_execution: {
			market: Address
			user: Address
			nonce: bigint
			deadline: bigint
		}
	}
	signature?: Hex
}

const DEFAULT_DEADLINE_SECONDS = 300

export function useClaimAll() {
	const config = useBreakoutConfig()
	const { wallets } = useWallets()
	const queryClient = useQueryClient()
	const executeGaslessClaimBatch = useAction(api.domains.trading.actions.executeGaslessClaimBatch)

	const [status, setStatus] = useState<ClaimAllStatus>("idle")
	const [progress, setProgress] = useState<ClaimAllProgress>({
		step: "",
		current: 0,
		total: 0,
		amountClaimed: 0,
		claimedMarkets: [],
		errors: [],
	})

	const claimAll = useCallback(
		async (positions: ClaimablePosition[]) => {
			if (positions.length === 0) {
				return
			}

			const wallet = findEmbeddedWallet(wallets)
			if (!wallet) {
				toast.error("No wallet connected")
				return
			}

			const walletClient = await createWalletClientFromWallet({ wallet })
			if (!isAddress(wallet.address)) {
				toast.error("Invalid wallet address")
				return
			}
			const userAddress = getAddress(wallet.address)

			// Reset state
			setStatus("preparing")
			setProgress({
				step: "Preparing claims...",
				current: 0,
				total: positions.length,
				amountClaimed: 0,
				claimedMarkets: [],
				errors: [],
			})

			// 1. PREPARE ALL CLAIMS IN PARALLEL
			const preparedClaims: PreparedClaim[] = []
			const preparationErrors: Array<{ market: Address; error: string }> = []

			const preparations = await Promise.allSettled(
				positions.map(async position => {
					const preparation = await prepareClaim(config.publicClient, {
						market: position.marketAddress,
						user: userAddress,
						chainId: config.chainId,
						deadlineSeconds: DEFAULT_DEADLINE_SECONDS,
					})
					return { marketAddress: position.marketAddress, preparation }
				}),
			)

			for (const [index, result] of preparations.entries()) {
				const position = positions[index]
				if (!position) {
					continue
				}
				if (result.status === "fulfilled") {
					preparedClaims.push(result.value)
				} else {
					preparationErrors.push({
						market: position.marketAddress,
						error: result.reason?.message || "Failed to prepare claim",
					})
				}
			}

			if (preparedClaims.length === 0) {
				setStatus("error")
				setProgress(prev => ({
					...prev,
					step: "All preparations failed",
					errors: preparationErrors,
				}))
				toast.error("Failed to prepare claims")
				return
			}

			// 2. SIGN ALL CLAIMS (sequential with embedded wallet - instant)
			setStatus("signing")
			setProgress(prev => ({
				...prev,
				step: "Signing claims...",
				current: 0,
				total: preparedClaims.length,
			}))

			for (const [index, claim] of preparedClaims.entries()) {
				setProgress(prev => ({ ...prev, current: index + 1 }))
				try {
					const signature = await walletClient.signTypedData({
						account: userAddress,
						domain: claim.preparation.signingPayload.domain,
						types: claim.preparation.signingPayload.types,
						primaryType: claim.preparation.signingPayload.primaryType,
						message: claim.preparation.signingPayload.message,
					})
					claim.signature = signature
				} catch (err) {
					preparationErrors.push({
						market: claim.marketAddress,
						error: err instanceof Error ? err.message : "Failed to sign",
					})
				}
			}

			const signedClaims = preparedClaims.filter(
				(c): c is PreparedClaim & { signature: Hex } => c.signature !== undefined,
			)
			if (signedClaims.length === 0) {
				setStatus("error")
				setProgress(prev => ({ ...prev, step: "Signing failed", errors: preparationErrors }))
				toast.error("Failed to sign claims")
				return
			}

			// 3. EXECUTE ALL CLAIMS IN ONE TRANSACTION
			setStatus("executing")
			setProgress(prev => ({
				...prev,
				step: "Claiming...",
				current: signedClaims.length,
				total: signedClaims.length,
			}))

			const claimedAddresses = new Set(signedClaims.map(c => c.marketAddress))
			const totalClaimable = positions
				.filter(p => claimedAddresses.has(p.marketAddress))
				.reduce((sum, p) => sum + p.claimableAmount, 0)
			const claimedMarkets = signedClaims.map(c => c.marketAddress)
			const executionErrors: Array<{ market: Address; error: string }> = []

			try {
				await executeGaslessClaimBatch({
					claims: signedClaims.map(c => ({
						preparation: serializeClaimPreparation(c.preparation),
						signature: c.signature,
					})),
				})

				// Invalidate cache for all claimed markets
				for (const marketAddress of claimedMarkets) {
					queryClient.invalidateQueries({
						queryKey: marketKeys.marketHasClaimed(marketAddress, userAddress),
					})
				}

				setProgress(prev => ({
					...prev,
					amountClaimed: totalClaimable,
					claimedMarkets: [...claimedMarkets],
				}))
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : "Unknown error"
				// All claims failed together since they're in one transaction
				for (const claim of signedClaims) {
					executionErrors.push({ market: claim.marketAddress, error: errorMessage })
				}

				captureTradeError(err instanceof Error ? err : new Error(errorMessage), {
					action: "claim",
					marketAddress: signedClaims[0]?.marketAddress,
					walletAddress: userAddress,
				})
			}

			// Invalidate user balance and positions
			queryClient.refetchQueries({
				queryKey: walletKeys.usdcBalance(userAddress),
			})
			queryClient.invalidateQueries({
				predicate: query =>
					query.queryKey[0] === "breakout" &&
					query.queryKey[1] === "userPositions" &&
					query.queryKey[2] === userAddress,
			})

			const allErrors = [...preparationErrors, ...executionErrors]
			const success = executionErrors.length === 0
			setStatus(success ? "completed" : "error")
			setProgress(prev => ({
				...prev,
				step: success ? "Done!" : "Failed",
				errors: allErrors,
			}))

			// Show result toast
			if (success) {
				toast.success(
					`Claimed $${totalClaimable.toFixed(2)} from ${claimedMarkets.length} position${claimedMarkets.length > 1 ? "s" : ""}`,
				)
			} else {
				toast.error("Failed to claim positions")
			}

			// Reset after delay
			setTimeout(() => {
				setStatus("idle")
				setProgress({
					step: "",
					current: 0,
					total: 0,
					amountClaimed: 0,
					claimedMarkets: [],
					errors: [],
				})
			}, 3000)

			return {
				totalClaimed: success ? totalClaimable : 0,
				claimedMarkets: success ? claimedMarkets : [],
				errors: allErrors,
			}
		},
		[wallets, config, executeGaslessClaimBatch, queryClient],
	)

	const reset = useCallback(() => {
		setStatus("idle")
		setProgress({
			step: "",
			current: 0,
			total: 0,
			amountClaimed: 0,
			claimedMarkets: [],
			errors: [],
		})
	}, [])

	return {
		claimAll,
		status,
		progress,
		reset,
		isClaiming: status !== "idle" && status !== "completed" && status !== "error",
	}
}
